import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { PROCESS_STEPS } from "@/lib/siteContent";
import PageHero from "@/components/site/PageHero";

const EASE = [0.16, 1, 0.3, 1];

export default function ProjetoInstalacaoPage() {
  useSeo(
    "Projeto e Instalação de Carregadores de VE | Dimensionamento Elétrico — HB Solar",
    "Do levantamento da infraestrutura ao comissionamento: conheça o processo completo de instalação de estações de recarga para veículos elétricos."
  );

  return (
    <>
      <PageHero
        testId="process-page-title"
        eyebrow="Projetos e Instalação"
        title="Do levantamento à operação, com engenharia responsável"
        desc="Cada estação de recarga é precedida de análise técnica da infraestrutura elétrica do local. Sem promessas vazias: potência definida por estudo, não por catálogo."
      />

      <section className="py-20 bg-[#FAFAF9]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div data-testid="process-steps" className="relative">
            <div className="absolute left-[27px] top-6 bottom-6 w-px bg-gradient-to-b from-amber-400 via-slate-200 to-transparent" />
            <div className="space-y-8">
              {PROCESS_STEPS.map((step, i) => (
                <motion.div
                  key={step.titulo}
                  initial={{ opacity: 0, x: 32 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: 0.04, ease: EASE }}
                  className="relative flex gap-6"
                >
                  <span className="z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#0B192C] font-display text-lg font-extrabold text-amber-400 shadow-lg">
                    {i + 1}
                  </span>
                  <div className="flex-1 rounded-2xl border border-slate-200/80 bg-white px-6 py-5 hover:shadow-[0_18px_36px_rgba(11,25,44,0.08)] transition-shadow duration-500">
                    <h2 className="font-display text-xl font-semibold tracking-tight text-[#0B192C]">{step.titulo}</h2>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.div
            data-testid="power-disclaimer"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mt-14 rounded-3xl border border-amber-300 bg-amber-50 p-8 sm:p-10"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-8 w-8 shrink-0 text-amber-500" />
              <div>
                <h2 className="font-display text-xl font-bold text-[#0B192C]">
                  Sobre a potência de carregamento
                </h2>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-700">
                  A potência efetivamente disponível para carregamento depende da
                  infraestrutura elétrica do local, demanda existente, capacidade de
                  transformadores, proteções, cabos, fornecimento da concessionária e
                  demais condições técnicas. Por isso,{" "}
                  <strong>nenhuma potência é prometida sem análise técnica prévia</strong>{" "}
                  — o que garante segurança e conformidade em cada projeto.
                </p>
                <Link
                  data-testid="process-simulate-cta"
                  to="/simule"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0B192C] px-6 py-3 text-sm font-extrabold text-white hover:bg-[#12243d] transition-colors duration-300"
                >
                  Simule seu projeto
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
